import React, { useState, useEffect, useRef } from "react";
import { Modal, Form, Input, Button, Select } from "antd";
import { BASE_URL, callApi } from "../../api/apiCaller";
import { GreenNotify, RedNotify } from "../../helper/helper";
import routes from "../../api/routes";
import configureAppStore from "../../redux/store";

const AddCategory = ({ visible, toggleModal, recordData }) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef(null);
  const { Option } = Select;
  const dummyImage =
    "https://novathreadbucket.s3.amazonaws.com/nova-app-1685176470232-dummy.PNG";
  const token = configureAppStore.getState().userDataSlice.token;
  useEffect(() => {
    if (visible) {
      if (recordData) {
        form.setFieldsValue({
          title: recordData?.title,
          category: recordData?.category._id,
          sub_category: recordData?.sub_category?._id,
          content: recordData?.content,
          exercises: recordData?.exercises,
        });
        setImage(recordData?.image || dummyImage);
      } else {
        form.resetFields();
        setImage(dummyImage);
      }
    }
  }, [visible, recordData, form]);

  useEffect(() => {
    if (visible) {
      callApi(
        "GET",
        `${routes.getAllCategories}`,
        null,
        setIsLoading,
        (res) => {
          if (res.status === 200) {
            setCategories(res?.data?.categories);
          } else {
            RedNotify("Error Occurred. Please try again later.");
          }
        },
        (error) => {
          RedNotify(
            "An error occurred during the API call. Please try again later.",
            error
          );
        }
      );
    }
  }, [visible]);

  const pickImageFile = () => {
    fileInputRef.current.click();
  };

  const uploadImageOnS3 = async (file) => {
    const myHeaders = new Headers();
    const formData = new FormData();
    formData.append("file", file);
    myHeaders.append("Accept", "application/json");
    myHeaders.append("Authorization", token);

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: formData,
      redirect: "follow",
    };
    return await new Promise((resolve, reject) => {
      fetch(`${BASE_URL}/uploadFile`, requestOptions)
        .then((response) => response.json())
        .then((result) => resolve(result))
        .catch((error) => reject(error));
    });
  };

  const handleFileChange = async (event) => {
    const fileList = event.target.files;
    if (fileList.length > 0) {
      const file = fileList[0];
      const validTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validTypes.includes(file.type)) {
        setFileError("Please select a valid image file (jpg, jpeg, png).");
        return;
      }
      try {
        const imgUrl = await uploadImageOnS3(file);
        setImage(imgUrl?.url);
        setFileError("");
      } catch (error) {
        console.error("Error uploading image:", error);
        setFileError("An error occurred while uploading the image.");
      }
    } else {
      setFileError("Please select a file.");
    }
  };

  const handleSave = () => {
    form
      .validateFields()
      .then((values) => {
        const requestBody = {
          ...values, // This already includes category and subcategory IDs
          image: image, // Include image URL if it's updated
        };

        const getRes = (res) => {
          if (res.status === 200) {
            GreenNotify(
              recordData
                ? "FaceYoga Updated Successfully"
                : "FaceYoga Created Successfully"
            );
            toggleModal();
          } else {
            RedNotify("Error Occurred. Please try again later.");
          }
        };

        const apiCall = recordData
          ? callApi(
              "PATCH",
              `${routes.updateFaceYoga}/${recordData._id}`,
              requestBody,
              setIsLoading,
              getRes
            )
          : callApi(
              "POST",
              `${routes.createFaceYoga}`,
              requestBody,
              setIsLoading,
              getRes
            );

        apiCall.catch((error) => {
          RedNotify(
            "An error occurred during the API call. Please try again later.",
            error
          );
        });
      })
      .catch((errorInfo) => {
        console.error("Validation Error:", errorInfo);
      });
  };

  const fetchSubCategories = (categoryId) => {
    let parent = categoryId;
    callApi(
      "GET",
      `${routes.getAllCategories}?parent=${parent}`,
      null,
      setIsLoading,
      (res) => {
        if (res.status === 200) {
          setSubCategories(res?.data?.categories);
        } else {
          RedNotify("Error Occurred. Please try again later.");
        }
      },
      (error) => {
        console.log("error fetching subcategories", error);
        RedNotify(
          "An error occurred during the API call. Please try again later.",
          error
        );
      }
    );
  };

  const handleCategoryChange = (value) => {
    fetchSubCategories(value);
  };

  return (
    <Modal
      title={recordData ? "Update FaceYoga" : "Create FaceYoga"}
      visible={visible}
      onCancel={toggleModal}
      footer={[
        <Button key="cancel" onClick={toggleModal}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          {recordData ? "Update" : "Save"}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Category"
          name="category"
          rules={[{ required: true, message: "Please select a category" }]}
        >
          <Select
            placeholder="Select a category"
            onChange={handleCategoryChange}
          >
            {categories.map((category) => {
              console.log("????::::", category);
              return (
                <Option key={category._id} value={category._id}>
                  {category?.title}
                </Option>
              );
            })}
          </Select>
        </Form.Item>
        <Form.Item
          label="Sub Category"
          name="sub_category"
          rules={[{ required: true, message: "Please select a sub category" }]}
        >
          <Select placeholder="Select a sub category">
            {subCategories?.map((subCategory) => (
              <Option key={subCategory._id} value={subCategory._id}>
                {subCategory.title}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Title"
          name="title"
          rules={[{ required: true, message: "Please enter the title" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Content"
          name="content"
          rules={[{ required: true, message: "Please enter content" }]}
        >
          <Input.TextArea autoSize />
        </Form.Item>
        <Form.Item
          label="Exercises"
          name="exercises"
          rules={[
            {
              required: true,
              message: "Please enter the number of exercises",
            },
            {
              pattern: /^\d+$/,
              message: "Exercises must be a number",
            },
          ]}
        >
          <Input />
        </Form.Item>

        <div
          onClick={pickImageFile}
          style={{ marginBottom: "2rem" }}
          className="add-product-modal-input-title"
        >
          <h2>Image</h2>
          <div className="add-product-modal-image">
            <img
              src={image || dummyImage}
              alt="Highlight"
              style={{ width: "100px", height: "100px", margin: "0 5px" }}
            />
          </div>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            required
          />
          {fileError && <p style={{ color: "red" }}>{fileError}</p>}
        </div>
      </Form>
    </Modal>
  );
};

export default AddCategory;
